import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

const getSeasonColor = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();

  const springStart = new Date(`${year}-03-20`);
  const summerStart = new Date(`${year}-06-21`);
  const autumnStart = new Date(`${year}-09-22`);
  const winterStart = new Date(`${year}-12-21`);

  if (date >= springStart && date < summerStart) return 'green'; // Spring
  if (date >= summerStart && date < autumnStart) return 'red'; // Summer
  if (date >= autumnStart && date < winterStart) return 'orange'; // Autumn
  return 'blue'; // Winter
};

export type ObservationData = {
  date: string;
  observationRate: number | null;
};

function aggregateDataMonthly(data: ObservationData[]): ObservationData[] {
  const monthlyData: { [key: string]: { sum: number; count: number } } = {};

  data.forEach(d => {
    if (d.observationRate !== null) {
      const date = new Date(d.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { sum: 0, count: 0 };
      }
      monthlyData[monthKey].sum += d.observationRate;
      monthlyData[monthKey].count += 1;
    }
  });

  return Object.keys(monthlyData).map(monthKey => {
    const [year, month] = monthKey.split('-').map(Number);
    const dateForMonth = new Date(year, month, 1);
    return {
      date: dateForMonth.toISOString().split('T')[0],
      observationRate: monthlyData[monthKey].count > 0 ? monthlyData[monthKey].sum / monthlyData[monthKey].count : null,
    };
  }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

function aggregateDataWeekly(data: ObservationData[]): ObservationData[] {
  const weeklyData: { [key: string]: { sum: number; count: number } } = {};

  data.forEach(d => {
    if (d.observationRate !== null) {
      const date = new Date(d.date);
      const dayOfWeek = date.getDay();
      const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const startOfWeek = new Date(date);
      startOfWeek.setDate(diff);
      const weekKey = `${startOfWeek.getFullYear()}-${startOfWeek.getMonth() + 1}-${startOfWeek.getDate()}`;

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { sum: 0, count: 0 };
      }
      weeklyData[weekKey].sum += d.observationRate;
      weeklyData[weekKey].count += 1;
    }
  });

  return Object.keys(weeklyData).map(weekKey => {
    const [year, month, day] = weekKey.split('-').map(Number);
    const dateForWeek = new Date(year, month - 1, day);
    return {
      date: dateForWeek.toISOString().split('T')[0],
      observationRate: weeklyData[weekKey].count > 0 ? weeklyData[weekKey].sum / weeklyData[weekKey].count : null,
    };
  }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

const AGGREGATION_THRESHOLDS = {
  ONE_YEAR: 365,
  THREE_YEARS: 1095,
} as const;

export default function ObservationRateGraph({ data }: { data: ObservationData[] }) {
  const sortedData = [...data].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  
  let displayData = sortedData;
  let isAggregated = false;
  let aggregationLevel: 'daily' | 'weekly' | 'monthly' = 'daily';

  if (sortedData.length > 0) {
    const firstDate = new Date(sortedData[0].date);
    const lastDate = new Date(sortedData[sortedData.length - 1].date);
    const timeDifference = lastDate.getTime() - firstDate.getTime();
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

    if (daysDifference <= AGGREGATION_THRESHOLDS.ONE_YEAR) {
      displayData = sortedData;
      isAggregated = false;
      aggregationLevel = 'daily';
    } else if (daysDifference <= AGGREGATION_THRESHOLDS.THREE_YEARS) {
      displayData = aggregateDataWeekly(sortedData);
      isAggregated = true;
      aggregationLevel = 'weekly';
    } else {
      displayData = aggregateDataMonthly(sortedData);
      isAggregated = true;
      aggregationLevel = 'monthly';
    }
  }

  const processedData = displayData
    .filter((d) => d.observationRate !== null)
    .map((d) => ({
      x: new Date(d.date).getTime(),
      y: d.observationRate as number,
    }));

  const options: ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        rotate: -45,
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM yyyy',
          day: 'dd MMM',
        },
      },
      tickAmount: Math.min(processedData.length, 10),
    },
    yaxis: {
      title: { text: 'Observations per Analyzed Hour' },
      labels: {
        formatter: (val: number) => val.toFixed(2),
      },
    },
    tooltip: {
      enabled: true,
      followCursor: true,
      theme: 'dark',
      fixed: {
        enabled: false
      },
      x: {
        formatter: function(val) {
          const date = new Date(val);
          if (aggregationLevel === 'monthly') {
            return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short' });
          } else if (aggregationLevel === 'weekly') {
            return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
          } else {
            return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
          }
        }
      },
      y: {
        formatter: (val: number) => `${val.toFixed(2)} obs/hr`,
      },
    },
    stroke: {
      curve: isAggregated ? 'straight' : 'smooth',
      width: isAggregated ? 2 : 1.5,
    },
    markers: {
      size: isAggregated ? 4 : 2,
      discrete: processedData.map((d, index) => ({
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: getSeasonColor(new Date(d.x).toISOString().split('T')[0]),
        strokeColor: getSeasonColor(new Date(d.x).toISOString().split('T')[0]),
        size: isAggregated ? 4 : 2,
      })),
    },
    grid: {
      show: true,
      borderColor: '#f1f1f1',
      strokeDashArray: 0,
      position: 'back',
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
  };

  const series = [
    {
      name: 'Observation rate',
      data: processedData,
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {/* Chart Container */}
      <div
        style={{
          width: '100%',
          maxWidth: '936px',
          marginBottom: '20px',
        }}
      >
        <ReactApexChart options={options} series={series} type="line" height={350} />
      </div>
    </div>
  );
}
